// rulesetSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";
const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Node Name Constants
const CATEGORY_CHECK = "Category Check";
const ALL_OF = "All Rules to Apply";
const ONE_OF = "Min One Rule to Apply";
const LAUNCH_PROTOCOL = "Launch Protocol";
const CHECK_RANGE = "Check Range";
const CHECK_PREVIOUS_RANGE = "Check Previous Range";
const AGE_CHECK = "Age Check";
const KEYWORD_CHECK = "Keyword Check";
const CHECK_MEDICATION_ISSUED = "Check Medication Issued";
const CALCULATE_QRISK = "Calculate QRisk";
const QRISK_RANGE_CHECK = "QRisk Range Check";
const LEAVE_FOR_GP = "Leave for GP";
const FILE_REPORT = "File Report";
const SEND_ACCURX_MESSAGE = "Send Accurx Message";
const CHANGE_OWNER = "Change Owner";
const ADD_CODE = "Add Code";
const CREATE_TASK = "Create Task";
const MEDICATION_PRESENT = "Medications Present";
const MEDICATION_NOT_PRESENT = "Medications Not Present";
const DIARY_ENTRY = "Diary Entry";

// Outcome Name Constants
const ALL_PASSED = "All Passed";
const FAILED = "Failed";
const PASSED = "Passed";
const NORMAL_REPORT = "Normal Report";
const ATLEAST_ONE_PASSED = "Atleast one passed";
const ALL_FAILED = "All Failed";
const IN_RANGE = "In Range";
const OUT_OF_RANGE = "Out of Range";
const IN_PER_RANGE = "In Range";
const OUT_OF_PER_RANGE = "Out of Range";
const IN_AGE_RANGE = "In Age Range";
const OUT_OF_AGE_RANGE = "Outside Age Range";
//const SUCCESS = "Success";

const INSIDE_QRISK_SCORE = "Inside QRisk Score";
const OUTSIDE_QRISK_SCORE = "Outside QRisk Score";
const DANGEROUS_LEADING_CHARS = /^[=+\-@#\/\\\t\r]/;
const SUBTEST_NAME_RE = /^[A-Za-z0-9 _.()\-#\/\\*]{1,100}$/;

const MAX_ALLOWED_AGE = 150;

export const GLOBAL_ERROR_KEY = "__ruleset__";

const MIN_RULE_NODE_TYPES = new Set([
  CHECK_RANGE,
  KEYWORD_CHECK,
  CHECK_MEDICATION_ISSUED,
]);

const NO_ACTIVE_RULE_MESSAGE =
  "No rules have been applied. Please add at least one active Check Range, Keyword Check, or Check Medication Issued rule before saving.";
const NO_OUTCOME_NODE_MESSAGE =
  "No outcome nodes have been added. At least one active node must be added under an outcome (at any level) before saving.";

// Save ruleset thunk
export const saveRuleset = createAsyncThunk(
  "ruleset/saveRuleset",
  async ({ ruleset, license_key, category }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/rulesets/save/`,
        {
          license_key,
          category,
          ruleset,
        },
      );
      return {
        message: res.data?.message || "Ruleset saved successfully",
      };
    } catch (err) {
      if (err.response && err.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchTaskMetadata = createAsyncThunk(
  "ruleset/fetchTaskMetadata",
  async ({ user_id, license_key, machine_uuid }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/get_clinician_and_task/`,
        {
          user_id,
          license_key,
          machine_uuid,
        },
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Unknown error",
      );
    }
  },
);

// Load ruleset thunk
export const loadRuleset = createAsyncThunk(
  "ruleset/loadRuleset",
  async ({ license_key, category }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/rulesets/load/`,
        {
          license_key,
          category,
        },
      );
      return {
        ruleset: res.data.ruleset.current_ruleset,
        subtest_names: res.data.subtest_names,
      };
    } catch (err) {
      if (err.response && err.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

function findRootCategoryNode(root) {
  if (!root) return null;

  if (root.node === CATEGORY_CHECK) {
    return root;
  }

  for (const child of root.nodes || []) {
    const found = findRootCategoryNode(child);
    if (found) return found;
  }

  for (const arr of Object.values(root.outcomes || {})) {
    for (const child of arr) {
      const found = findRootCategoryNode(child);
      if (found) return found;
    }
  }

  return null;
}

function walkNodes(node, callback) {
  if (!node) return;

  callback(node);

  for (const child of node.nodes || []) {
    walkNodes(child, callback);
  }

  for (const arr of Object.values(node.outcomes || {})) {
    for (const child of arr) {
      walkNodes(child, callback);
    }
  }
}

function hasActiveRule(node) {
  if (!node || node.fields?.deactivated === true) return false;

  if (MIN_RULE_NODE_TYPES.has(node.node)) return true;

  for (const child of node.nodes || []) {
    if (hasActiveRule(child)) return true;
  }
  for (const arr of Object.values(node.outcomes || {})) {
    for (const child of arr) {
      if (hasActiveRule(child)) return true;
    }
  }
  return false;
}

export function getOutcomeConflicts(nodes = []) {
  const active = (nodes || []).filter(
    (n) => n && n.fields?.deactivated !== true,
  );
  const count = (type) => active.filter((n) => n.node === type).length;
  const has = (type) => count(type) > 0;
  const msgs = [];

  // Change Owner can't be combined with File Report / Add Code / Send Accurx
  if (has(CHANGE_OWNER)) {
    [FILE_REPORT, DIARY_ENTRY, ADD_CODE, SEND_ACCURX_MESSAGE].forEach((t) => {
      if (has(t)) {
        msgs.push(
          `"${CHANGE_OWNER}" cannot be combined with "${t}" in the same outcome.`,
        );
      }
    });
  }

  // Leave for GP can't be combined with File Report, and only one allowed
  if (has(LEAVE_FOR_GP) && has(FILE_REPORT)) {
    msgs.push(
      `"${LEAVE_FOR_GP}" cannot be combined with "${FILE_REPORT}" in the same outcome.`,
    );
  }
  if (count(LEAVE_FOR_GP) > 1) {
    msgs.push(`Only one "${LEAVE_FOR_GP}" is allowed per outcome.`);
  }

  // Multiple File Reports allowed only if they are identical
  const reports = active.filter((n) => n.node === FILE_REPORT);
  if (reports.length > 1) {
    const signatures = new Set(
      reports.map((n) => {
        const approach = n.fields?.filing_approach ?? "";
        const comment =
          approach === "With Comment"
            ? (n.fields?.filing_comment ?? "").trim()
            : "";
        return `${approach}||${comment}`;
      }),
    );
    if (signatures.size > 1) {
      msgs.push(
        `Multiple "${FILE_REPORT}" nodes in the same outcome must use the same filing approach and comment.`,
      );
    }
  }

  return msgs;
}

function collectOutcomeConflicts(root) {
  const all = [];
  walkNodes(root, (node) => {
    for (const [key, arr] of Object.entries(node.outcomes || {})) {
      getOutcomeConflicts(arr).forEach((m) =>
        all.push(`${node.node} → Outcome "${key}": ${m}`),
      );
    }
  });
  return all;
}

function walkActiveNodes(node, callback) {
  if (!node || node.fields?.deactivated === true) return;
  callback(node);
  for (const child of node.nodes || []) walkActiveNodes(child, callback);
  for (const arr of Object.values(node.outcomes || {})) {
    for (const child of arr) walkActiveNodes(child, callback);
  }
}

function hasActiveOutcomeNode(node) {
  if (!node || node.fields?.deactivated === true) return false;
  const outcomeArrays = Object.values(node.outcomes || {});
  if (
    outcomeArrays.some((arr) =>
      arr.some((c) => c && c.fields?.deactivated !== true),
    )
  )
    return true;
  return (
    (node.nodes || []).some(hasActiveOutcomeNode) ||
    outcomeArrays.some((arr) => arr.some(hasActiveOutcomeNode))
  );
}

function collectStructureErrors(root) {
  const errs = [];
  if (!hasActiveOutcomeNode(root)) errs.push(NO_OUTCOME_NODE_MESSAGE);

  walkActiveNodes(root, (node) => {
    const name = node.fields?.protocol_name
      ? ` "${node.fields.protocol_name}"`
      : "";

    if (node.node === CALCULATE_QRISK) {
      const hasRange = (node.nodes || []).some(
        (c) => c.node === QRISK_RANGE_CHECK && c.fields?.deactivated !== true,
      );
      if (!hasRange) {
        errs.push(
          `${CALCULATE_QRISK}${name}: at least one "${QRISK_RANGE_CHECK}" must be added as a child node.`,
        );
      }
    }

    if (
      node.node === LAUNCH_PROTOCOL &&
      Object.keys(node.outcomes || {}).length === 0
    ) {
      errs.push(
        `${LAUNCH_PROTOCOL}${name}: at least one outcome must be specified.`,
      );
    }
  });
  return errs;
}

function syncGlobalErrors(state) {
  const g = {};
  if (!hasActiveRule(state.root)) g.rules = NO_ACTIVE_RULE_MESSAGE;
  const conflicts = collectOutcomeConflicts(state.root);
  if (conflicts.length) g.outcomes = conflicts;
  const structure = collectStructureErrors(state.root);
  if (structure.length) g.structure = structure;

  if (Object.keys(g).length) state.errors[GLOBAL_ERROR_KEY] = g;
  else delete state.errors[GLOBAL_ERROR_KEY];
}

const validators = {
  [LAUNCH_PROTOCOL]: (fields) => {
    const errors = {};
    if (!fields.protocol_name || fields.protocol_name.trim() === "") {
      errors.protocol_name = "Protocol Name is required";
    }
    return errors;
  },
  [AGE_CHECK]: (fields) => {
    const errors = {};

    const parseAge = (raw) => {
      if (raw === undefined || raw === null || String(raw).trim() === "") {
        return { empty: true, value: null };
      }
      const str = String(raw).trim();
      if (!/^\d+$/.test(str)) return { empty: false, value: NaN };
      return { empty: false, value: parseInt(str, 10) };
    };

    const min = parseAge(fields.min_age);
    const max = parseAge(fields.max_age);

    if (min.empty) {
      errors.min_age = "Min Age is required";
    } else if (Number.isNaN(min.value)) {
      errors.min_age = "Min Age must be a non-negative whole number";
    } else if (min.value > MAX_ALLOWED_AGE) {
      errors.min_age = `Min Age cannot exceed ${MAX_ALLOWED_AGE}`;
    }

    if (max.empty) {
      errors.max_age = "Max Age is required";
    } else if (Number.isNaN(max.value)) {
      errors.max_age = "Max Age must be a non-negative whole number";
    } else if (max.value > MAX_ALLOWED_AGE) {
      errors.max_age = `Max Age cannot exceed ${MAX_ALLOWED_AGE}`;
    }

    if (
      !errors.min_age &&
      !errors.max_age &&
      min.value !== null &&
      max.value !== null &&
      min.value > max.value
    ) {
      errors.max_age = "Max Age must be greater than or equal to Min Age";
    }

    return errors;
  },
  [KEYWORD_CHECK]: (fields) => {
    const errors = {};

    if (!fields.check_type || fields.check_type.trim() === "") {
      errors.check_type = "Check type is required";
    }

    if (!fields.keywords || String(fields.keywords).trim() === "") {
      errors.keywords = "Keywords is required";
    }

    if (!fields.source || fields.source.trim() === "") {
      errors.source = "Source is required";
    }

    return errors;
  },
  [CHECK_RANGE]: (fields, root) => {
    const errors = {};

    const rawSubtest = fields.subtest || "";
    const subtest = rawSubtest.trim();

    if (!subtest) {
      errors.subtest = "Subtest is required";
    } else if (DANGEROUS_LEADING_CHARS.test(subtest)) {
      errors.subtest =
        "Subtest cannot start with =, +, -, @, #, /, \\, or a tab/carriage return character";
    } else if (!SUBTEST_NAME_RE.test(subtest)) {
      errors.subtest =
        "Subtest may only contain letters, numbers, spaces, and . _ ( ) * - # / \\";
    }
    const min =
      fields.min !== undefined && fields.min !== ""
        ? parseFloat(fields.min)
        : null;
    const max =
      fields.max !== undefined && fields.max !== ""
        ? parseFloat(fields.max)
        : null;

    if (min === null || max === null) {
      errors.min = "Min and Max value must be specified";
      errors.max = "Min and Max value must be specified";
    }

    if (min !== null && max !== null) {
      if (!(min === 0 && max === 0) && min >= max) {
        errors.max = "Max must be greater than Min";
      }
    }

    if (min !== null && min < 0) errors.min = "Min value cannot be negative";
    if (max !== null && max < 0) errors.max = "Max value cannot be negative";

    // follow_lab validation from CATEGORY_CHECK root node
    const categoryNode = findRootCategoryNode(root);

    if (categoryNode?.fields?.follow_lab) {
      const parsedLabMin = parseFloat(fields.lab_min);
      const labMin = Number.isNaN(parsedLabMin) ? null : parsedLabMin;

      const parsedLabMax = parseFloat(fields.lab_max);
      const labMax = Number.isNaN(parsedLabMax) ? null : parsedLabMax;

      if (labMin !== null && min !== null && min !== labMin) {
        errors.min =
          "Clinician Min Val must match Lab Min Val while Automatically follow and file lab min max is enabled";
      }

      if (labMax !== null && max !== null && max !== labMax) {
        errors.max =
          "Clinician Max Val must match Lab Max Val while Automatically follow and file lab min max is enabled";
      }
    }

    const minAge =
      fields.min_age !== undefined && fields.min_age !== ""
        ? parseInt(fields.min_age)
        : null;
    const maxAge =
      fields.max_age !== undefined && fields.max_age !== ""
        ? parseInt(fields.max_age)
        : null;

    if (minAge !== null && String(minAge) !== fields.min_age) {
      errors.min_age = "Min Age must be a positive whole number";
    }
    if (maxAge !== null && String(maxAge) !== fields.max_age) {
      errors.max_age = "Max Age must be a positive whole number";
    }

    if (minAge !== null) {
      if (!Number.isInteger(minAge) || minAge < 0) {
        errors.min_age = "Min Age must be a positive whole number";
      }
    }

    if (maxAge !== null) {
      if (!Number.isInteger(maxAge) || maxAge < 0) {
        errors.max_age = "Max Age must be a positive whole number";
      }
    }

    if (
      (minAge !== null && maxAge === null) ||
      (minAge === null && maxAge !== null)
    ) {
      errors.min_age = "Both Min Age and Max Age must be specified";
      errors.max_age = "Both Min Age and Max Age must be specified";
    }

    if (maxAge === 0) {
      errors.max_age = "Max Age should not be zero";
    }

    if (minAge !== null && maxAge !== null) {
      if (minAge >= maxAge) {
        errors.max_age =
          "Max Age must be greater than Min Age (they cannot be the same)";
      }
    }

    return errors;
  },

  [CHECK_PREVIOUS_RANGE]: (fields) => {
    const errors = {};

    const rawSubtest = fields.subtest || "";
    const subtest = rawSubtest.trim();

    if (!subtest) {
      errors.subtest = "Subtest is required";
    } else if (DANGEROUS_LEADING_CHARS.test(subtest)) {
      errors.subtest =
        "Subtest cannot start with =, +, -, @, #, /, \\, or a tab/carriage return character";
    } else if (!SUBTEST_NAME_RE.test(subtest)) {
      errors.subtest =
        "Subtest may only contain letters, numbers, spaces, and . _ ( ) - # / \\";
    }
    const dropPerc =
      fields.drop_perc !== undefined && fields.drop_perc !== ""
        ? parseFloat(fields.drop_perc)
        : null;
    const gainPerc =
      fields.gain_perc !== undefined && fields.gain_perc !== ""
        ? parseFloat(fields.gain_perc)
        : null;

    if (dropPerc === null && gainPerc === null) {
      errors.drop_perc = "At least one of Drop % or Gain % must be specified";
      errors.gain_perc = "At least one of Drop % or Gain % must be specified";
    }

    if (dropPerc !== null && dropPerc < 0) {
      errors.drop_perc = "Drop % cannot be negative";
    }

    if (gainPerc !== null && gainPerc < 0) {
      errors.gain_perc = "Gain % cannot be negative";
    }

    if (!fields.previous_x_days_or_report) {
      errors.previous_x_days_or_report =
        "Search type must be specified (X Previous Days or Previous Report or Check Trend)";
    }

    if (
      fields.previous_x_days_or_report === "X Previous Days" ||
      fields.previous_x_days_or_report === "Check Trend"
    ) {
      if (!fields.previous_x_value || fields.previous_x_value === "") {
        errors.previous_x_value = "X Value must be specified";
      } else {
        const xValue = parseInt(fields.previous_x_value);
        if (String(xValue) !== fields.previous_x_value) {
          errors.previous_x_value = "X Value must be a positive whole number";
        }
        if (!Number.isInteger(xValue) || xValue <= 0) {
          errors.previous_x_value = "X Value must be a positive integer";
        }
      }
    }

    return errors;
  },

  [CHECK_MEDICATION_ISSUED]: (fields) => {
    const errors = {};

    if (
      !fields.issued_in_months ||
      String(fields.issued_in_months).trim() === ""
    ) {
      errors.issued_in_months =
        "Medication Issued in Last (Months) is required";
    } else {
      const months = parseInt(fields.issued_in_months, 10);

      if (!Number.isInteger(months) || months <= 0) {
        errors.issued_in_months = "Must be a positive integer";
      }
    }

    if (
      !fields.medications ||
      typeof fields.medications !== "string" ||
      fields.medications.trim() === ""
    ) {
      errors.medications = "At least one medication must be specified";
    }

    return errors;
  },

  [CALCULATE_QRISK]: (fields) => {
    const errors = {};
    if (!fields.protocol_name || fields.protocol_name.trim() === "") {
      errors.protocol_name = "Protocol Name is required";
    }
    return errors;
  },

  [FILE_REPORT]: (fields) => {
    const errors = {};

    if (!fields.filing_approach) {
      errors.filing_approach =
        "Filing Approach must be specified (With Comment or No Comments)";
    }

    if (fields.filing_approach === "With Comment") {
      if (!fields.filing_comment || fields.filing_comment.trim() === "") {
        errors.filing_comment =
          "Filing Comment is required when 'With Comment' is selected";
      }
    }
    return errors;
  },

  [CHANGE_OWNER]: (fields) => {
    const errors = {};
    if (!fields.new_owner || fields.new_owner.trim() === "") {
      errors.new_owner = "Change Owner To name is required";
    }
    return errors;
  },

  [ADD_CODE]: (fields) => {
    const errors = {};
    if (!fields.code || fields.code.trim() === "") {
      errors.code = "Code is required";
    }
    return errors;
  },
  [DIARY_ENTRY]: (fields) => {
    const errors = {};

    if (!fields.code || String(fields.code).trim() === "") {
      errors.code = "Code is required";
    }
    if (!fields.hcp || String(fields.hcp).trim() === "") {
      errors.hcp = "HCP is required";
    }
    if (!fields.diary_text || String(fields.diary_text).trim() === "") {
      errors.diary_text = "Diary Text is required";
    }

    // Exactly one of Diary Date / Patient Age
    const dateStr = String(fields.diary_date ?? "").trim();
    const ageStr = String(fields.diary_age ?? "").trim();
    const hasDate = dateStr !== "";
    const hasAge = ageStr !== "";

    if (!hasDate && !hasAge) {
      const msg = "Specify either a Diary Date or a Patient Age";
      errors.diary_date = msg;
      errors.diary_age = msg;
    } else if (hasDate && hasAge) {
      const msg = "Specify only one of Diary Date or Patient Age";
      errors.diary_date = msg;
      errors.diary_age = msg;
    } else if (hasDate) {
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(dateStr) ||
        Number.isNaN(new Date(`${dateStr}T00:00:00`).getTime())
      ) {
        errors.diary_date = "Diary Date must be a valid date";
      }
    } else {
      if (!/^\d+$/.test(ageStr)) {
        errors.diary_age = "Patient Age must be a non-negative whole number";
      } else if (parseInt(ageStr, 10) > MAX_ALLOWED_AGE) {
        errors.diary_age = `Patient Age cannot exceed ${MAX_ALLOWED_AGE}`;
      }
    }

    return errors;
  },

  [CREATE_TASK]: (fields) => {
    const errors = {};
    if (!fields.task_type || fields.task_type.trim() === "") {
      errors.task_type = "Task Type is required";
    }
    const due = (fields.due_date || "").trim();
    if (!due) {
      errors.due_date = "Due Date is required";
    } else if (
      !/^\d{4}-\d{2}-\d{2}$/.test(due) ||
      Number.isNaN(new Date(`${due}T00:00:00`).getTime())
    ) {
      errors.due_date = "Due Date must be a valid date";
    }
    if (!fields.notes || fields.notes.trim() === "") {
      errors.notes = "Notes is required";
    }
    if (!fields.clinician_name || fields.clinician_name.trim() === "") {
      errors.clinician_name = "Assign to is required";
    }
    return errors;
  },

  [LEAVE_FOR_GP]: (fields) => {
    const errors = {};
    if (!fields.info_var || fields.info_var.trim() === "") {
      errors.info_var = "Info is required";
    }
    return errors;
  },
  [SEND_ACCURX_MESSAGE]: (fields) => {
    const errors = {};
    if (!fields.accurx_message || fields.accurx_message.trim() === "") {
      errors.accurx_message = "Message is required";
    }
    return errors;
  },
  [QRISK_RANGE_CHECK]: (fields) => {
    const errors = {};

    const minVal =
      fields.min_val !== undefined && fields.min_val !== ""
        ? parseFloat(fields.min_val)
        : null;
    const maxVal =
      fields.max_val !== undefined && fields.max_val !== ""
        ? parseFloat(fields.max_val)
        : null;

    if (minVal === null) {
      errors.min_val = "QRisk Minimum value must be specified";
    }
    if (maxVal === null) {
      errors.max_val = "QRisk Maximum value must be specified";
    }
    if (minVal !== null && maxVal !== null) {
      if (minVal > maxVal) {
        errors.max_val = "QRisk Maximum must be greater than Minimum";
      }
    }
    return errors;
  },
};

// Fresh CHECK_RANGE node from a suggestion row
function buildCheckRangeFromSuggestion(suggestion, baseNode) {
  return {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    node: CHECK_RANGE,
    nodes: [],
    outcomes: {},
    fields: {
      subtest: baseNode.fields?.subtest ?? "",
      min: suggestion.min_val != null ? String(suggestion.min_val) : "",
      max: suggestion.max_val != null ? String(suggestion.max_val) : "",
      gender: suggestion.gender || "Both",
      ...(suggestion.min_age != null
        ? { min_age: String(suggestion.min_age) }
        : {}),
      ...(suggestion.max_age != null
        ? { max_age: String(suggestion.max_age) }
        : {}),
      deactivated: false,
    },
  };
}

// Find the PARENT of a node with a given id.
// Returns { parent, key, index } where:
//   parent – the parent node object (mutably)
//   key    – "nodes" | outcome key string
//   index  – position inside the array
function findParentOf(root, targetId) {
  function walk(current) {
    // Check direct children
    if (current.nodes) {
      for (let i = 0; i < current.nodes.length; i++) {
        if (current.nodes[i].id === targetId) {
          return { parent: current, key: "nodes", index: i };
        }
        const found = walk(current.nodes[i]);
        if (found) return found;
      }
    }
    // Check outcome children
    if (current.outcomes) {
      for (const [outcomeKey, arr] of Object.entries(current.outcomes)) {
        for (let i = 0; i < arr.length; i++) {
          if (arr[i].id === targetId) {
            return { parent: current, key: outcomeKey, index: i };
          }
          const found = walk(arr[i]);
          if (found) return found;
        }
      }
    }
    return null;
  }
  return walk(root);
}

const rulesetSlice = createSlice({
  name: "ruleset",
  initialState: {
    root: {
      id: "root",
      node: CATEGORY_CHECK,
      nodes: [],
      outcomes: {},
    },
    subtest_names: [],
    errors: {
      [GLOBAL_ERROR_KEY]: {
        rules: NO_ACTIVE_RULE_MESSAGE,
        structure: [NO_OUTCOME_NODE_MESSAGE],
      },
    },
    success: null,
    clinicians: [],
    taskTypes: [],
    nodeConfigs: {
      [CATEGORY_CHECK]: {
        template: (values) => {
          let parts = ["."];
          if (values.skip_without_ranges)
            parts.push(" Will be skipped if no lab ranges are present.");
          if (values.file_normal) parts.push(" Will file for normal report.");
          return parts.join(" ");
        },
        has_needsreview: true,
        has_inreview: true,
        fields_config: [
          {
            label: "Inreview:",
            var: "inreview",
            widget: "TextField",
            type: "string",
          },
          {
            label: "Needs Review and Approval:",
            var: "needs_review",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Skip if no lab ranges are present:",
            var: "skip_without_ranges",
            widget: "CheckBox",
            type: "boolean",
            messageon:
              'When selecting - "Skip if no lab ranges are present" option, ' +
              "MyBotGP will not file the test result if no subtest lines with ranges are found." +
              "A minimum of one subtest line must be present to file.",
            messageoff:
              'When de-selecting - "Skip if no lab ranges are present" option, ' +
              "MyBotGP will file the test result even if no subtest lines with ranges are found." +
              "No subtest lines need to be present to file.",
          },
          {
            label: "Follow Lab File Normal & Update Clinician Values",
            var: "follow_lab",
            widget: "CheckBox",
            type: "boolean",
            messageon:
              'When selecting - Please note by selecting the "Follow Lab File Normal & Update Clinician Values" option, ' +
              "MyBotGP will update existing subtest ranges with lab min and max values. The category will continue to be enabled.",
            messageoff:
              'When de-selecting - Please note by de-selecting the "Follow Lab File Normal & Update Clinician Values" option, ' +
              "MyBotGP will update existing subtest lab ranges and remove the catebory to In Review.",
            validate: ({ value, root }) => {
              // Only validate when enabling
              if (!value) {
                return { valid: true };
              }

              let invalidNode = null;

              walkNodes(root, (node) => {
                if (invalidNode) return;

                if (node.node !== CHECK_RANGE) return;

                const min =
                  node.fields?.min !== undefined && node.fields?.min !== ""
                    ? parseFloat(node.fields.min)
                    : null;

                const max =
                  node.fields?.max !== undefined && node.fields?.max !== ""
                    ? parseFloat(node.fields.max)
                    : null;

                const labMin =
                  node.fields?.lab_min !== undefined &&
                  node.fields?.lab_min !== ""
                    ? parseFloat(node.fields.lab_min)
                    : null;

                const labMax =
                  node.fields?.lab_max !== undefined &&
                  node.fields?.lab_max !== ""
                    ? parseFloat(node.fields.lab_max)
                    : null;

                if (
                  (labMin !== null && min !== null && min !== labMin) ||
                  (labMax !== null && max !== null && max !== labMax)
                ) {
                  invalidNode = node;
                }
              });

              if (invalidNode) {
                return {
                  valid: false,
                  message:
                    `All Check Range nodes must have clinician ranges equal to lab ranges before enabling Automatically follow and file lab min max.\n\n` +
                    `Failed node: ${invalidNode.fields?.subtest || invalidNode.id}`,
                };
              }

              return { valid: true };
            },
          },
          {
            label: "All Subtests needs to evaluate a Range Check:",
            var: "all_subtests_evaluated",
            widget: "CheckBox",
            type: "boolean",
          },
        ],
        validate: () => ({}),
        child_nodes: true,
        outcomes: [ALL_PASSED, FAILED, NORMAL_REPORT],
        children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          AGE_CHECK,
        ],
        outcome_children: [
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          DIARY_ENTRY,
        ],
      },

      [ALL_OF]: {
        template: () => "All conditions must pass",
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
        ],
        validate: () => ({}),
        child_nodes: true,
        outcomes: [ALL_PASSED, FAILED],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          AGE_CHECK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },

      [ONE_OF]: {
        template: () => "At least one condition must pass",
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
        ],
        validate: () => ({}),
        child_nodes: true,
        outcomes: [ATLEAST_ONE_PASSED, ALL_FAILED],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          AGE_CHECK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },
      [AGE_CHECK]: {
        template: (values) => {
          const hasMin =
            values.min_age !== undefined &&
            values.min_age !== null &&
            values.min_age !== "";
          const hasMax =
            values.max_age !== undefined &&
            values.max_age !== null &&
            values.max_age !== "";

          if (hasMin && hasMax)
            return `Check age is between ${values.min_age} and ${values.max_age} (inclusive)`;
          if (hasMin) return `Check age is at least ${values.min_age}`;
          if (hasMax) return `Check age is at most ${values.max_age}`;
          return AGE_CHECK;
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Min Age:",
            var: "min_age",
            widget: "TextField",
            type: "int",
            width: 80,
          },
          {
            label: "Max Age:",
            var: "max_age",
            widget: "TextField",
            type: "int",
            width: 80,
          },
        ],
        validate: validators[AGE_CHECK],
        child_nodes: false, // no child nodes
        outcomes: [IN_AGE_RANGE, OUT_OF_AGE_RANGE],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          AGE_CHECK,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },
      [CREATE_TASK]: {
        template: () => "Create task",
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Task type:",
            var: "task_type",
            widget: "TextField",
            type: "string",
            width: 200,
          },
          {
            label: "Urgent:",
            var: "urgent",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Due date:",
            var: "due_date",
            widget: "DateField",
            type: "date",
            width: 200,
          },
          {
            label: "Notes:",
            var: "notes",
            widget: "TextField",
            type: "string",
            width: 200,
          },
          // {
          //   label: "Task Category:",
          //   var: "task_category",
          //   widget: "TextField",
          //   type: "string",
          //   width: 200,
          // },
          {
            label: "Assign to:",
            var: "clinician_name",
            widget: "TextField",
            type: "string",
            width: 200,
          },
        ],
        validate: validators[CREATE_TASK],
        child_nodes: false,
      },

      [LAUNCH_PROTOCOL]: {
        template: (values) =>
          values.protocol_name
            ? `Launch Protocol: ${values.protocol_name}`
            : LAUNCH_PROTOCOL,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Protocol Name:",
            var: "protocol_name",
            widget: "TextField",
            type: "string",
            width: 200,
          },
        ],
        validate: validators[LAUNCH_PROTOCOL],
        child_nodes: false,
        outcomes: [ALL_PASSED],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },

      [CHECK_RANGE]: {
        template: (values) => {
          const parts = [];
          if (values.subtest) parts.push(`Check subtest "${values.subtest}"`);
          if (values.min != null && values.max != null)
            parts.push(`in range ${values.min} – ${values.max}`);
          else if (values.min != null) parts.push(`with minimum ${values.min}`);
          else if (values.max != null) parts.push(`with maximum ${values.max}`);
          if (values.gender && values.gender !== "Both")
            parts.push(`for ${values.gender}`);
          if (values.min_age != null || values.max_age != null)
            parts.push(
              `in age range ${values.min_age ?? "?"} – ${values.max_age ?? "?"}`,
            );
          return parts.join(", ");
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Subtest:",
            var: "subtest",
            widget: "AutoComplete",
            type: "string",
          },
          {
            label: "Lab Min Value:",
            var: "lab_min",
            widget: "TextField",
            type: "float",
            width: 80,
            editable: false,
          },
          {
            label: "Lab Max Value:",
            var: "lab_max",
            widget: "TextField",
            type: "float",
            width: 80,
            editable: false,
          },
          {
            label: "Min Value:",
            var: "min",
            widget: "TextField",
            type: "float",
            width: 80,
          },
          {
            label: "Max Value:",
            var: "max",
            widget: "TextField",
            type: "float",
            width: 80,
          },
          {
            label: "Min Age:",
            var: "min_age",
            widget: "TextField",
            type: "int",
            width: 80,
          },
          {
            label: "Max Age:",
            var: "max_age",
            widget: "TextField",
            type: "int",
            width: 80,
          },
          {
            label: "Gender:",
            var: "gender",
            widget: "Combobox",
            values: ["Male", "Female", "Both"],
            defaultValue: "Both",
            allowEmpty: false,
          },
          {
            label: "Suggest Ranges:",
            var: null, // action widget
            widget: "RangeRecommendButton",
          },
          {
            label: "Lab Range History:",
            var: null,
            widget: "LabHistoryButton",
          },
        ],
        validate: validators[CHECK_RANGE],
        child_nodes: true,
        outcomes: [IN_RANGE, OUT_OF_RANGE],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          AGE_CHECK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },

      [CHECK_PREVIOUS_RANGE]: {
        template: (values) => {
          const parts = [];

          if (values.subtest && values.subtest.trim() !== "") {
            parts.push(`Check subtest "${values.subtest}"`);
          }

          if (
            values.drop_perc !== null &&
            values.drop_perc !== undefined &&
            values.drop_perc !== ""
          ) {
            parts.push(`drop ≥ ${values.drop_perc}%`);
          }

          if (
            values.gain_perc !== null &&
            values.gain_perc !== undefined &&
            values.gain_perc !== ""
          ) {
            parts.push(`gain ≥ ${values.gain_perc}%`);
          }

          const prefix =
            values.previous_x_days_or_report === "Check Trend"
              ? "trend over"
              : "over";

          if (
            values.previous_x_days_or_report === "X Previous Days" ||
            values.previous_x_days_or_report === "Check Trend"
          ) {
            if (
              values.previous_x_value !== null &&
              values.previous_x_value !== undefined &&
              values.previous_x_value !== ""
            ) {
              parts.push(`${prefix} ${values.previous_x_value} days`);
            }
          } else if (
            values.previous_x_days_or_report &&
            values.previous_x_days_or_report.trim() !== ""
          ) {
            parts.push(`${prefix} ${values.previous_x_days_or_report}`);
          }

          return parts.join(", ");
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Subtest:",
            var: "subtest",
            widget: "AutoComplete",
            type: "string",
          },
          {
            label: "Drop %:",
            var: "drop_perc",
            widget: "TextField",
            type: "int",
          },
          {
            label: "Gain %:",
            var: "gain_perc",
            widget: "TextField",
            type: "int",
          },
          {
            label: "Search type:",
            var: "previous_x_days_or_report",
            widget: "RadioButton",
            type: "text",
            values: ["X Previous Days", "Previous Report", "Check Trend"],
          },
          {
            label: "X Value:",
            var: "previous_x_value",
            widget: "TextField",
            type: "int",
          },
        ],
        validate: validators[CHECK_PREVIOUS_RANGE],
        child_nodes: true,
        outcomes: [IN_PER_RANGE, OUT_OF_PER_RANGE],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          AGE_CHECK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },

      [LEAVE_FOR_GP]: {
        template: (values) =>
          values.info_var
            ? `Leave for GP. Info: ${values.info_var}`
            : LEAVE_FOR_GP,
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Info:", var: "info_var" },
        ],
        validate: validators[LEAVE_FOR_GP],
        child_nodes: false,
      },
      [FILE_REPORT]: {
        template: (values) => {
          if (values.filing_approach === "No Comments")
            return "File report without comments";
          if (values.filing_comment)
            return `File report with comment: "${values.filing_comment}"`;
          return "File report";
        },
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Filing Approach:",
            var: "filing_approach",
            widget: "RadioButton",
            type: "text",
            values: ["With Comment", "No Comments"],
          },
          {
            label: "Filing Comment:",
            var: "filing_comment",

            widget: "AutoComplete",
            type: "string",
            options: [
              "Normal No Action",
              "** Normal",
              "** Abnormal",
              "** Satisfactory",
              "*Expected/stable",
              "*On correct treatment",
            ],
          },
        ],
        validate: validators[FILE_REPORT],
        child_nodes: false,
      },
      [DIARY_ENTRY]: {
        template: (values) => {
          const when = values.diary_date
            ? `on ${values.diary_date}`
            : values.diary_age !== undefined &&
                values.diary_age !== null &&
                values.diary_age !== ""
              ? `at patient age ${values.diary_age}`
              : "";
          const parts = ["Add diary entry"];
          if (values.code) parts.push(`code "${values.code}"`);
          if (when) parts.push(when);
          if (values.hcp) parts.push(`for HCP ${values.hcp}`);
          return parts.join(", ");
        },
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Code:",
            var: "code",
            widget: "TextField",
            type: "string",
            width: 200,
          },
          {
            label: "Diary Date:",
            var: "diary_date",
            widget: "DateField",
            type: "date",
            width: 200,
          },
          {
            label: "Patient Age:",
            var: "diary_age",
            widget: "TextField",
            type: "int",
            width: 120,
          },
          {
            label: "HCP:",
            var: "hcp",
            widget: "TextField",
            type: "string",
            width: 200,
          },
          {
            label: "Diary Text:",
            var: "diary_text",
            widget: "TextArea",
            type: "string",
            width: 500,
            rows: 4,
          },
        ],
        validate: validators[DIARY_ENTRY],
        child_nodes: false,
      },

      [SEND_ACCURX_MESSAGE]: {
        template: (values) =>
          values.accurx_message
            ? `Send Accurx message: "${values.accurx_message}"`
            : SEND_ACCURX_MESSAGE,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Message:", var: "accurx_message" },
        ],
        validate: validators[SEND_ACCURX_MESSAGE],
        child_nodes: false,
      },

      [KEYWORD_CHECK]: {
        template: (values) => {
          const parts = [KEYWORD_CHECK];
          if (values.keywords) parts.push(`for keywords: "${values.keywords}"`);
          if (values.check_type) parts.push(`must be ${values.check_type}`);
          if (values.source) parts.push(`in ${values.source}`);
          return parts.join(", ");
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Keywords to Check:",
            var: "keywords",
            widget: "KeywordBuilder",
          },
          {
            label: "Check type:",
            var: "check_type",
            widget: "RadioButton",
            type: "text",
            values: ["Present", "Absent"],
          },
          {
            label: "Source:",
            var: "source",
            widget: "RadioButton",
            type: "text",
            values: ["Report", "Problem Page"],
          },
        ],
        validate: validators[KEYWORD_CHECK],
        child_nodes: true,
        outcomes: [FAILED, PASSED],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          AGE_CHECK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },

      [CHECK_MEDICATION_ISSUED]: {
        template: (values) => {
          const months = values.issued_in_months
            ? `within last ${values.issued_in_months} months`
            : "";

          const medicationText = values.medications
            ? `for medication: ${values.medications}`
            : "";

          return [CHECK_MEDICATION_ISSUED, medicationText, months]
            .filter(Boolean)
            .join(", ");
        },

        has_deactivated: true,
        has_terminate: true,

        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Medication Issued in Last (Months):",
            var: "issued_in_months",
            widget: "TextField",
            type: "int",
          },

          {
            label: "Medications to Check:",
            var: "medications",
            widget: "KeywordBuilder",
            title: "Medications to check",
          },
        ],
        validate: validators[CHECK_MEDICATION_ISSUED],
        child_nodes: true,
        outcomes: [MEDICATION_PRESENT, MEDICATION_NOT_PRESENT],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          AGE_CHECK,
        ],

        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },

      [CALCULATE_QRISK]: {
        template: (values) =>
          values.protocol_name
            ? `Calculate QRisk using protocol ${values.protocol_name}`
            : CALCULATE_QRISK,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Protocol Name", var: "protocol_name" },
        ],
        validate: validators[CALCULATE_QRISK],
        child_nodes: true,
        children: [QRISK_RANGE_CHECK],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },

      [QRISK_RANGE_CHECK]: {
        template: (values) => {
          if (values.min_val != null && values.max_val != null)
            return `Check QRisk value in range ${values.min_val} – ${values.max_val}`;
          if (values.min_val != null)
            return `Check QRisk value above ${values.min_val}`;
          if (values.max_val != null)
            return `Check QRisk value below ${values.max_val}`;
          return "Check QRisk value";
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "QRisk Minimum Value:",
            var: "min_val",
            widget: "TextField",
            type: "float",
            width: 120,
          },
          {
            label: "QRisk Maximum Value:",
            var: "max_val",
            widget: "TextField",
            type: "float",
            width: 120,
          },
        ],
        validate: validators[QRISK_RANGE_CHECK],
        child_nodes: false,
        outcomes: [INSIDE_QRISK_SCORE, OUTSIDE_QRISK_SCORE],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          DIARY_ENTRY,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
          AGE_CHECK,
        ],
      },

      [CHANGE_OWNER]: {
        template: (values) =>
          values.new_owner
            ? `Change Owner to ${values.new_owner}`
            : CHANGE_OWNER,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Change Owner To:", var: "new_owner" },
        ],
        validate: validators[CHANGE_OWNER],
        child_nodes: false,
      },

      [ADD_CODE]: {
        template: (values) =>
          values.code ? `Add code "${values.code}" to Care Record` : ADD_CODE,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Code to Add:", var: "code" },
        ],
        validate: validators[ADD_CODE],
        child_nodes: false,
      },
    },
  },

  reducers: {
    addChildNode: (state, action) => {
      const { parentId, nodeType } = action.payload;
      const newNode = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        node: nodeType,
        nodes: [],
        outcomes: {},
        fields: {},
      };

      function insertNode(current) {
        if (current.id === parentId) {
          if (!current.nodes) current.nodes = [];
          current.nodes.push(newNode);
          return true;
        }
        if (current.nodes && current.nodes.some((child) => insertNode(child)))
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => insertNode(child)),
          );
        }
        return false;
      }

      insertNode(state.root);
      syncGlobalErrors(state);
    },

    addOutcomeBranch: (state, action) => {
      const { nodeId, outcomeKey } = action.payload;

      function addBranch(current) {
        if (current.id === nodeId) {
          if (!current.outcomes) current.outcomes = {};
          if (!current.outcomes[outcomeKey]) current.outcomes[outcomeKey] = [];
          return true;
        }
        if (current.nodes && current.nodes.some((child) => addBranch(child)))
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => addBranch(child)),
          );
        }
        return false;
      }

      addBranch(state.root);
      syncGlobalErrors(state);
    },

    addOutcomeNode: (state, action) => {
      const { parentId, outcomeKey, nodeType } = action.payload;

      const newNode = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        node: nodeType,
        nodes: [],
        outcomes: {},
        fields: {},
      };

      function insertOutcomeNode(current) {
        if (current.id === parentId) {
          if (!current.outcomes[outcomeKey]) current.outcomes[outcomeKey] = [];
          current.outcomes[outcomeKey].push(newNode);
          return true;
        }
        if (
          current.nodes &&
          current.nodes.some((child) => insertOutcomeNode(child))
        )
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => insertOutcomeNode(child)),
          );
        }
        return false;
      }

      insertOutcomeNode(state.root);
      syncGlobalErrors(state);
    },

    updateNodeField: (state, action) => {
      const { nodeId, field, value } = action.payload;

      function updateNode(current) {
        if (current.id === nodeId) {
          current.fields[field] = value;

          // run validation after update
          const cfg = state.nodeConfigs[current.node];
          if (cfg && typeof cfg.validate === "function") {
            const errors = cfg.validate(
              current.fields || {},
              state.root,
              current,
            );
            state.errors[nodeId] = errors;
          } else {
            state.errors[nodeId] = {};
          }
          return true;
        }
        if (current.nodes && current.nodes.some((child) => updateNode(child)))
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => updateNode(child)),
          );
        }
        return false;
      }

      updateNode(state.root);
      syncGlobalErrors(state);
    },

    deleteNode: (state, action) => {
      const { nodeId } = action.payload;

      // Handle root deletion explicitly
      if (state.root?.id === nodeId) {
        state.root = {};
        state.errors = {};
        return;
      }

      function removeNode(current) {
        if (current.nodes) {
          const idx = current.nodes.findIndex((child) => child.id === nodeId);
          if (idx !== -1) {
            current.nodes.splice(idx, 1);
            delete state.errors[nodeId];
            return true;
          }
          current.nodes.forEach((child) => removeNode(child));
        }
        if (current.outcomes) {
          Object.values(current.outcomes).forEach((arr) => {
            const idx = arr.findIndex((child) => child.id === nodeId);
            if (idx !== -1) {
              arr.splice(idx, 1);
              delete state.errors[nodeId];
            } else {
              arr.forEach((child) => removeNode(child));
            }
          });
        }
      }

      removeNode(state.root);
      syncGlobalErrors(state);
    },

    deleteOutcomeBranch: (state, action) => {
      const { nodeId, outcomeKey } = action.payload;

      function removeBranch(current) {
        if (current.id === nodeId && current.outcomes) {
          const branchNodes = current.outcomes[outcomeKey] || [];
          branchNodes.forEach((node) => delete state.errors[node.id]);
          delete current.outcomes[outcomeKey];
          return true;
        }
        if (current.nodes && current.nodes.some((child) => removeBranch(child)))
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => removeBranch(child)),
          );
        }
        return false;
      }

      removeBranch(state.root);
      syncGlobalErrors(state);
    },
    setNodeErrors: (state, action) => {
      const { nodeId, errors } = action.payload;
      state.errors[nodeId] = errors;
    },

    // applySuggestions
    // payload: { nodeId: string, suggestions: Array<SuggestionRow> }
    //   1. Find the CHECK_RANGE node (nodeId) and its parent.
    //   2. Build new CHECK_RANGE nodes from the selected suggestions.
    //   3a. If only 1 suggestion  → replace the target node in-place.
    //   3b. If 2+ suggestions:
    //       - Check if the PARENT is already a ONE_OF ("Min One Rule to Apply").
    //         If YES  → remove the target node and splice the new nodes into
    //                     the same position inside the existing ONE_OF.
    //         If NO   → remove the target node and insert a NEW ONE_OF node
    //                     at the same position, with the new CHECK_RANGE nodes
    //                     as its children.
    applySuggestions: (state, action) => {
      const { nodeId, suggestions } = action.payload;

      if (!suggestions || suggestions.length === 0) return;

      // Find the original CHECK_RANGE node
      function findNode(current, id) {
        if (current.id === id) return current;
        for (const child of current.nodes || []) {
          const f = findNode(child, id);
          if (f) return f;
        }
        for (const arr of Object.values(current.outcomes || {})) {
          for (const child of arr) {
            const f = findNode(child, id);
            if (f) return f;
          }
        }
        return null;
      }

      const originalNode = findNode(state.root, nodeId);
      if (!originalNode) return;

      // Build replacement CHECK_RANGE nodes.
      // The first node inherits children/outcomes of the original node
      const newNodes = suggestions.map((suggestion, idx) => {
        const base = buildCheckRangeFromSuggestion(suggestion, originalNode);
        if (idx === 0) {
          // preserve the original's outcome branches & child nodes
          base.nodes = originalNode.nodes ?? [];
          base.outcomes = originalNode.outcomes ?? {};
        }
        return base;
      });

      if (suggestions.length === 1) {
        // ── Case A: single suggestion → replace in-place ─────────────────
        const result = findParentOf(state.root, nodeId);
        if (!result) return;
        const { parent, key, index } = result;

        const targetArray =
          key === "nodes" ? parent.nodes : parent.outcomes[key];
        targetArray.splice(index, 1, newNodes[0]);

        // clean up old errors, seed new node with no errors
        delete state.errors[nodeId];
        state.errors[newNodes[0].id] = {};
      } else {
        // ── Case B: multiple suggestions ──────────────────────────────────
        const result = findParentOf(state.root, nodeId);
        if (!result) return;
        const { parent, key, index } = result;

        const targetArray =
          key === "nodes" ? parent.nodes : parent.outcomes[key];

        if (parent.node === ONE_OF) {
          // Parent is already a ONE_OF → reuse it: remove the original node
          // and splice all new nodes in its place.
          targetArray.splice(index, 1, ...newNodes);
        } else {
          // Parent is NOT a ONE_OF → create a new ONE_OF wrapper and replace
          // the original CHECK_RANGE node with it.
          const oneOfNode = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            node: ONE_OF,
            nodes: newNodes,
            outcomes: {},
            fields: {},
          };
          targetArray.splice(index, 1, oneOfNode);
          state.errors[oneOfNode.id] = {};
        }

        // Clean up errors for the removed original node; init new nodes.
        delete state.errors[nodeId];
        newNodes.forEach((n) => {
          state.errors[n.id] = {};
        });
      }
      syncGlobalErrors(state);
    },
  },

  extraReducers: (builder) => {
    builder
      // Save
      .addCase(saveRuleset.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(saveRuleset.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload?.message || "Ruleset saved successfully";
      })

      .addCase(saveRuleset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.status || "Failed to save ruleset";
        state.success = null;
      })
      .addCase(fetchTaskMetadata.pending, () => {})

      .addCase(fetchTaskMetadata.fulfilled, (state, action) => {
        state.clinicians = action.payload.clinician_list || [];
        state.taskTypes = action.payload.task_list || [];
      })

      .addCase(fetchTaskMetadata.rejected, (state, action) => {})
      // Load
      .addCase(loadRuleset.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(loadRuleset.fulfilled, (state, action) => {
        state.loading = false;
        state.root = action.payload.ruleset;
        state.subtest_names = action.payload.subtest_names;
        state.errors = {};
        syncGlobalErrors(state);
      })
      .addCase(loadRuleset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load ruleset";
        state.success = null;
      });
  },
});

export const {
  addChildNode,
  addOutcomeBranch,
  addOutcomeNode,
  updateNodeField,
  deleteNode,
  deleteOutcomeBranch,
  setNodeErrors,
  applySuggestions,
} = rulesetSlice.actions;

export default rulesetSlice.reducer;
